<svelte:window on:click={clickHandler} on:keydown={keyDown} />

<div class="prom-combo {cssClass}">
    {#if label}
        <label class="prom-combo__label-text" id={labelID} for={id}>
            {label}
            {#if required}
                <span aria-hidden="true" class="prom-input__required-star">*</span>
            {/if}
        </label>
    {/if}

    <div class="prom-combo__container"
        class:prom-combo_has_error={hasError}>

        <ComboboxValueContainer bind:this={container}
            {id}
            {dataProvider}
            {enableAutoComplete}
            {enableInlineAutoComplete}
            {expanded}
            {listboxFocusedID}
            {chevronVisible}
            {required}
            on:filter={applyFilter}
            on:commit={event => commitNewValue(event.detail)}
            on:closePopup={closePopup} />

        {#if expanded}
            <ComboboxPopupContainer bind:this={popup}
                {id}
                options={internalOptions}
                {selected}
                on:select={select}
                on:returnFocusAfterKeyPressed={returnFocusAfterKeyPressed}
                on:focusedOptionChanged={focusedOptionChanged}
                on:activedescendantChanged={activedescendantChanged}/>
        {/if}
    </div>


    {#if hasError && errorMessage}
        <div class="prom-combo__error-message" id={errorMessageID}>
            {errorMessage}
        </div>
    {/if}
</div>

<script>
    import { createEventDispatcher, tick } from "svelte";

    import ComboboxValueContainer from "./ComboboxValueContainer.svelte";
    import ComboboxPopupContainer from "./ComboboxPopupContainer.svelte";

    import { normalizeKey } from "@itslearning/atlas/keyboard/normalize";
    import { Keys } from "@itslearning/atlas/keyboard/keys";

    import { ComboboxStandardDataProvider } from "./standard.provider";

    /** @type {string} */
    export let id = "rnd" + Math.random().toString(36);

    /** @type {String}*/
    export let label;

    /** @type {(String | Object)[]} */
    export let options = [];

    /** @type {boolean} */
    export let required = false;

    export let selected = undefined;

    /** @type {Boolean} */
    export let enableNewValues = false;

    /** @type {Boolean} */
    export let enableAutoComplete = true;

    /** @type {Boolean} */
    export let enableInlineAutoComplete = false;

    /** @type {string} */
    export let cssClass = '';

    /** @type {Boolean} */
    export let hasError = false;

    /** @type {string} */
    export let errorMessage = undefined;

    /** @type {boolean} */
    export let chevronVisible = true;

    /** @type {Object} */
    export let dataProvider = new ComboboxStandardDataProvider(options);


    /** @type {Object[]} */
    let internalOptions = undefined;

    // $: visibleOptions = getVisibleOptions(selectOnly, enableAutoComplete, filter);
    $: errorMessageID = `errormessage_${id}`;

    let prevOptions;
    $: {
        if (prevOptions !== options) {
            if (dataProvider.setOptions) {
                dataProvider.setOptions(options);
            }
            prevOptions = options;
        }
    }

    $: {
        if (!options) {
            console.warn('Options is required');
        }
    }

    let prevSelected = undefined;
    $: {
        if (prevSelected !== selected) {
            setDisplayValue();
            prevSelected = selected;
        }
    }

    $: labelID = 'label_' + id;

    /** @type {ComboboxValueContainer} */
    let container;

    /** @type {ComboboxPopupContainer} */
    let popup;

    let expanded = false;
    let filter = '';
    let listboxFocusedID = undefined;
    let displayValue;

    const dispatch = createEventDispatcher();

    async function setDisplayValue() {
        displayValue = await getDisplayValue(selected);
        if (container) {
            container.setDisplayValue(displayValue);
        }
    }

    async function clickHandler(event) {
        if (container.containsTarget(event.target)) {
            if (!expanded) {
                internalOptions = await requestForOptions();
            }
            expanded = !expanded;
            if (expanded && internalOptions.length === 0) {
                expanded = false;
            }
        } else {
            if (expanded) {
                commitNewValue(container.getStrValue());
            } else {
                if (container.checkIfEmpty()) {
                    selected = undefined;
                }
            }
            closePopup();
        }
    }

    async function select(event) {
        const newValue = event.detail.value;
        await changeValue(newValue);
        await tick();
        closePopup();
        filter = '';
        container.focus();
    }

    async function resetFilter() {
        await setFilter('');
    }

    async function setFilter(filterValue) {
        filter = filterValue;
    }

    async function changeValue(newValue) {
        container.setValue(newValue);

        if (newValue !== selected) {
            selected = newValue;
            dispatch('change', selected);
            filter = '';
        }
    }

    async function reset() {
        container.reset();
        changeValue(undefined);
        await resetFilter();
    }

    async function getDisplayValue(value) {
        if (!options) {
            return '';
        }

        if (value === null || value === undefined) {
            return '';
        }

        return await Promise.resolve(dataProvider.getNameById(value));
    }

    function keyDown(event) {
        switch (normalizeKey(event.key)) {
            case Keys.TAB:
                if (!internalOptions) {
                    return;
                }
                closePopup();
                if (checkIfFocused()) {
                    commitNewValue(container.getStrValue());
                }
                break;

            case Keys.ESCAPE: {
                const shouldBeFocused = expanded;
                closePopup();
                if (shouldBeFocused) {
                    container.focus();
                }
                break;
            }
            case Keys.DOWN:
            case Keys.UP:
            case Keys.ENTER:
            case Keys.SPACE: {
                openPopupByKey(event);
                break;
            }
        }
    }

    function openPopupByKey(event) {
        if (checkIfFocused() && !expanded) {
            const key = normalizeKey(event.key);
            if (key === Keys.UP || key === Keys.DOWN) {
                event.preventDefault();
            }
            if ((key === Keys.ENTER || key === Keys.SPACE)) {
                return;
            }

            openPopup();
        }
    }

    async function openPopup() {
        await setFilter();
        internalOptions = await requestForOptions();
        if (internalOptions.length > 0) {
            expanded = true;
        }
    }

    function closePopup() {
        if (expanded) {
            expanded = false;
            listboxFocusedID = undefined;
        }
    }

    function checkIfFocused() {
        return container.checkIfFocused() || (popup && popup.checkIfFocused());
    }

    async function applyFilter(event) {
        const oldFilter = filter || '';
        filter = event.detail;
        filter = filter.toLocaleLowerCase();
        internalOptions = await requestForOptions(filter);
        if (filter.length > oldFilter.length && internalOptions.length > 0) {
            expanded = true;
        }
        dispatch('filterChanged', event.detail);
    }

    async function commitNewValue(strValue) {
        if (!strValue) {
            reset();
        } else {
            const item = internalOptions.find(e => e.label.toLocaleLowerCase() === strValue.toLocaleLowerCase());
            if (item) {
                changeValue(item.value);
            } else if (enableNewValues) {
                const item = await Promise.resolve(dataProvider.createNewItem(strValue));
                changeValue(item.value);
            } else {
                reset();
            }

            closePopup();
            await tick();
            filter = '';
            container.focus();
        }
    }

    async function requestForOptions(searchText) {
        const options = await Promise.resolve(dataProvider.getOptions(searchText));

        return options;
    }

    function returnFocusAfterKeyPressed() {
        container.focus();
    }

    function focusedOptionChanged(event) {
        const option = event.detail;

        if (enableInlineAutoComplete) {
            container.setValue(option.label);
        }

        container.focus();
    }

    function activedescendantChanged(event) {
        listboxFocusedID = event.detail;
    }
</script>
